/**
 * Credit Note Mapper
 * Maps between API DTOs and Domain Entities
 */

import {
  CreditNoteEntity,
  CreditNoteDataEntity,
  CreditNotesResultEntity,
  CreditNoteCountsEntity,
  PaginationInfo,
  CreditsAppliedResultEntity,
  CreditAppliedEntity,
} from "../../domain/entities/CreditNoteEntity";

/**
 * Helper function to parse credit note JSON if it's a string
 */
export const parseCreditNoteJson = (
  creditNoteJsonString: string | CreditNoteDataEntity
): CreditNoteDataEntity | null => {
  if (!creditNoteJsonString) {
    return null;
  }

  try {
    if (typeof creditNoteJsonString === "string") {
      return JSON.parse(creditNoteJsonString) as CreditNoteDataEntity;
    }
    return creditNoteJsonString;
  } catch (error) {
    console.error("Error parsing credit note json:", error);
    return null;
  }
};

const toOptionalNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeCreditNoteJson = (
  json: CreditNoteDataEntity | null
): CreditNoteDataEntity | null => {
  if (!json) return null;

  return {
    ...json,
    sub_total: toOptionalNumber(json.sub_total),
    tax_total: toOptionalNumber(json.tax_total),
    total: toOptionalNumber(json.total),
    line_items: Array.isArray(json.line_items)
      ? json.line_items.map((item) => ({
          ...item,
          quantity: toOptionalNumber(item.quantity),
          rate: toOptionalNumber(item.rate),
          item_total: toOptionalNumber(item.item_total),
          tax_percentage: toOptionalNumber(item.tax_percentage),
          tax_amount: toOptionalNumber(item.tax_amount),
          tds_tax_percentage: toOptionalNumber(item.tds_tax_percentage),
          tds_tax_amount: toOptionalNumber(item.tds_tax_amount),
        }))
      : json.line_items,
  };
};

export class CreditNoteMapper {
  /**
   * Map API response to CreditNoteEntity
   */
  static toCreditNoteEntity(apiResponse: any): CreditNoteEntity {
    const json = normalizeCreditNoteJson(
      parseCreditNoteJson(apiResponse.json || apiResponse.credit_note_json || {})
    );

    return {
      id: apiResponse.id || apiResponse.credit_note_id || 0,
      jobid: apiResponse.jobid || null,
      companyid: apiResponse.company_id || apiResponse.companyid || null,
      filename: apiResponse.filename || null,
      original_filename: apiResponse.original_filename || null,
      file_size: apiResponse.file_size || null,
      folder_id: apiResponse.folder_id || null,
      json: json || ({} as CreditNoteDataEntity),
      reviewed: apiResponse.reviewed?.toString() || "0",
      status: apiResponse.status?.toString() || "0",
      is_under_review: apiResponse.is_under_review ?? 0,
      zoho_id: apiResponse.zoho_id || null,
      posted_to_zoho_date: apiResponse.posted_to_zoho_date || null,
      document_number: apiResponse.document_number || null,
      deleted: apiResponse.deleted || 0,
      created_at: apiResponse.created_at || "",
      updated_at: apiResponse.updated_at || "",
      signed_url: apiResponse.signed_url || null,
      signed_url_expires_at: apiResponse.signed_url_expires_at || null,
      // ZOUR-19 review 12: the server's GST answer for this note (parent
      // invoice's date, or the parent bill's for a vendor credit). Named
      // explicitly because this mapper builds the entity field by field.
      gst: apiResponse.gst ?? undefined,
    };
  }

  /**
   * Map API response to CreditNotesResultEntity
   */
  static toCreditNotesResultEntity(apiResponse: any): CreditNotesResultEntity {
    const creditNotes: CreditNoteEntity[] = [];
    const paginationData =
      apiResponse.data?.pagination || apiResponse.pagination || {};

    if (
      apiResponse.data?.credit_notes &&
      Array.isArray(apiResponse.data.credit_notes)
    ) {
      creditNotes.push(
        ...apiResponse.data.credit_notes.map((item: any) =>
          this.toCreditNoteEntity(item)
        )
      );
    } else if (Array.isArray(apiResponse.data)) {
      creditNotes.push(
        ...apiResponse.data.map((item: any) => this.toCreditNoteEntity(item))
      );
    }

    const pagination: PaginationInfo = {
      total: paginationData.total || 0,
      limit: paginationData.limit || 0,
      offset: paginationData.offset || 0,
      has_more: paginationData.has_more || false,
      current_page:
        paginationData.current_page ||
        Math.floor((paginationData.offset || 0) / (paginationData.limit || 1)) +
          1,
      total_pages:
        paginationData.total_pages ||
        Math.ceil((paginationData.total || 0) / (paginationData.limit || 1)),
    };

    return {
      creditNotes,
      pagination,
    };
  }

  /**
   * Map API response to CreditNoteCountsEntity
   */
  static toCreditNoteCountsEntity(apiResponse: any): CreditNoteCountsEntity {
    return {
      pendingCount: Number(apiResponse.pending_count) || 0,
      postedCount: Number(apiResponse.posted_count) || 0,
      rejectedCount: Number(apiResponse.rejected_count) || 0,
      underReviewCount: Number(apiResponse.under_review_count) || 0,
    };
  }

  /**
   * Map API response to CreditsAppliedResultEntity
   */
  static toCreditsAppliedResultEntity(
    apiResponse: any
  ): CreditsAppliedResultEntity {
    console.log("Mapping credits applied, apiResponse:", apiResponse);
    // Handle both direct response and nested response structure
    const creditsData = apiResponse.credits || apiResponse || [];
    console.log("Credits data:", creditsData);
    const credits: CreditAppliedEntity[] = Array.isArray(creditsData)
      ? creditsData.map((credit: any) => {
          console.log("Mapping credit:", credit);
          return {
            creditnote_id: credit.creditnote_id,
            vendor_credit_id: credit.vendor_credit_id,
            creditnotes_number: credit.creditnotes_number,
            vendor_credit_number: credit.vendor_credit_number,
            credited_date: credit.credited_date,
            date: credit.date,
            amount_applied: credit.amount_applied,
            amount: credit.amount,
            creditnotes_invoice_id: credit.creditnotes_invoice_id,
            vendor_credit_bill_id: credit.vendor_credit_bill_id,
          };
        })
      : [];

    console.log("Mapped credits:", credits);
    const result = {
      credits,
      isVendorCredit: apiResponse.is_vendor_credit || false,
    };
    console.log("Final credits applied result:", result);
    return result;
  }

  /**
   * Map credit note entity with credits applied
   */
  static toCreditNoteEntityWithCredits(apiResponse: any): CreditNoteEntity {
    console.log("toCreditNoteEntityWithCredits - apiResponse:", apiResponse);
    // Handle both direct response and nested data structure
    const responseData = apiResponse.data || apiResponse;
    console.log("responseData:", responseData);
    const creditNote = this.toCreditNoteEntity(
      responseData.credit_note || responseData
    );

    // Extract credits applied from response
    if (responseData.credits_applied) {
      console.log(
        "Found credits_applied in response:",
        responseData.credits_applied
      );
      creditNote.creditsApplied = this.toCreditsAppliedResultEntity(
        responseData.credits_applied
      );
      console.log(
        "Set creditsApplied on credit note:",
        creditNote.creditsApplied
      );
    } else {
      console.log("No credits_applied found in responseData");
    }

    return creditNote;
  }
}
